import { useEffect } from "react";
import type { ApiActivityDetail } from "@/lib/types";

interface SessionsModalProps {
    open: boolean;
    sessions: ApiActivityDetail["sessions"];
    onClose: () => void;
}

export default function SessionsModal({
    open,
    sessions,
    onClose,
}: SessionsModalProps) {
    useEffect(() => {
        if (!open) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/40"
            role="dialog"
            aria-modal="true"
            aria-label="Toutes les séances"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="w-full h-full bg-white overflow-hidden flex flex-col md:w-full md:max-w-md md:h-auto md:max-h-[80vh] md:rounded-xl md:shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-background-200 shrink-0">
                    <div className="flex items-center gap-2">
                        <i className="ri-repeat-line text-foreground-400"></i>

                        <h2 className="text-base font-semibold text-foreground-900">
                            Toutes les séances
                        </h2>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:bg-background-100 hover:text-foreground-700 transition-colors cursor-pointer"
                        aria-label="Fermer"
                    >
                        <i className="ri-close-line text-lg"></i>
                    </button>
                </div>

                {/* Sessions */}
                <div className="overflow-y-auto p-4">
                    <div className="flex flex-col gap-2">
                        {sessions.map((session) => (
                            <div
                                key={session.id}
                                className="flex items-center justify-between gap-3 rounded-lg bg-background-50 px-3 py-2.5"
                            >
                                <span className="text-sm text-foreground-700">
                                    {new Date(session.startsAt).toLocaleDateString("fr-FR", {
                                        weekday: "long",
                                        day: "numeric",
                                        month: "short",
                                    })}
                                    {" · "}
                                    {new Date(session.startsAt).toLocaleTimeString("fr-FR", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    })}
                                </span>

                                {session.endsAt && (
                                    <span className="shrink-0 text-xs text-foreground-400">
                                        →
                                        {" "}
                                        {new Date(session.endsAt).toLocaleTimeString("fr-FR", {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}